'use client';

import type { FC } from 'react';
import { ErrorMessage } from '@/components/ErrorMessage';
import { EmptyState } from '@/components/ui';
import { CaseCard } from '@/components/cases/CaseCard';
import { CaseCardSkeleton } from '@/components/CaseCardSkeleton';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useCaseLinkHref } from '@/hooks/useCaseLinkHref';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useCasesQuery } from '@/queries/useCasesQuery';
import { pinForLanguage } from '@/components/cases/catalog/catalogSort';
import translations from './CasesScreenMarketing.translations';

const GRID_CLASS =
  'grid pt-4 grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] gap-1 lg:grid-cols-4 lg:gap-3';
const FIRST_ROW_DESKTOP = 4;
// WHY: ширина карточки по сетке выше: от lg — 4 колонки в колонке 896 px, мобильная — две колонки до 520 px, дальше
// колонок больше, а карточка не шире 242 px
const CARD_IMAGE_SIZES = '(min-width: 1024px) 208px, (min-width: 520px) 242px, calc(50vw - 18px)';

export const CasesScreenMarketing: FC = () => {
  const { t, lang } = useLocalTranslation(translations);
  const { data: cases = [], isLoading, error, refetch } = useCasesQuery();
  const showSkeleton = isLoading && cases.length === 0;
  const caseLink = useCaseLinkHref();
  // WHY: на мобиле шапка, лента дропов, баннер и статистика уводят первый ряд ниже первого экрана — preload там не нужен ни одной карточке
  const priorityCards = useIsDesktop() ? FIRST_ROW_DESKTOP : 0;

  if (showSkeleton) {
    return (
      <div className={GRID_CLASS}>
        {[...Array(8)].map((_, index) => (
          <CaseCardSkeleton key={`skeleton-${index}`} />
        ))}
      </div>
    );
  }

  if (error && cases.length === 0) {
    return (
      <ErrorMessage onRetry={() => refetch()} />
    );
  }

  if (cases.length === 0) {
    return <EmptyState message={t('empty')} />;
  }

  return (
    <div className={GRID_CLASS}>
      {pinForLanguage(cases, lang).map((caseItem, index) => (
        <CaseCard
          key={caseItem.id}
          id={caseItem.id}
          name={caseItem.name}
          imageUrl={caseItem.imageUrl}
          imagePlaceholder={caseItem.imagePlaceholder}
          priceInCredits={caseItem.priceInCredits}
          openingsCount={caseItem.openingsCount}
          isNew={caseItem.isNew}
          href={caseLink(caseItem)}
          priority={index < priorityCards}
          imageSizes={CARD_IMAGE_SIZES}
        />
      ))}
    </div>
  );
};
